import { initialsFromName } from './initials.js';
import { resolveIsCustomer } from './customerClassification.js';
import { displayEventType } from '../constants/eventTypes.js';
import { applyTraceFixture } from '../constants/traceFixtures.js';

/**
 * Normalizes the event owner (My Team, CT-4858). Returns `undefined` when the
 * field is absent (My Work — no owner concept) and `null` when the field is
 * present but empty (team event, unassigned). The UI keys off that split:
 * `undefined` hides the owner chip and team actions; `null` shows Unassigned.
 * `isMine` is computed client-side (assigneeUuid === token user uuid) or set
 * optimistically by team actions.
 */
function mapOwner(owner) {
  if (owner === undefined) return undefined;
  if (!owner) return null;
  return {
    id: owner.id ?? owner.user_id,
    name: owner.name,
    initials: owner.initials ?? initialsFromName(owner.name),
    isMine: owner.is_mine ?? owner.isMine ?? false,
  };
}

/** Governed-entity classes that always escalate an HTL Continuation to Critical. */
const CRITICAL_GE_CLASSES = new Set(['GE-COMM', 'GE-FIN', 'GE-COMP']);
const CONTINUATION_TYPES = new Set(['htl_continuation', 'client_continuation']);
const LOW_CONFIDENCE_THRESHOLD = 0.65;

/**
 * HTL Continuation severity rule (CT-4859): force Critical when the GE class is
 * GE-COMM/GE-FIN/GE-COMP OR confidence < 0.65, regardless of the API severity.
 */
function resolveSeverity(raw, detail) {
  const type = raw.type ?? raw.event_type;
  // Break Glass is always Critical — emergency access is the most time-sensitive
  // event in the inbox, regardless of the severity the API returns.
  if (type === 'gov_break_glass') return 'critical';
  if (!CONTINUATION_TYPES.has(type)) return raw.severity;
  if (raw.severity === 'act_now') return raw.severity;
  const geClass = detail.geClassification ?? detail.ge_classification;
  const confidence = detail.confidence ?? detail.confidence_score;
  const forced =
    (geClass && CRITICAL_GE_CLASSES.has(geClass)) ||
    (typeof confidence === 'number' && confidence < LOW_CONFIDENCE_THRESHOLD);
  return forced ? 'critical' : raw.severity;
}

function mapDetail(detail = {}) {
  return {
    ...detail,
    confidence: detail.confidence ?? detail.confidence_score,
    geClassification: detail.geClassification ?? detail.ge_classification,
    ucpProfileUrl: detail.ucpProfileUrl ?? detail.ucp_profile_url,
    traceRef: detail.traceRef ?? detail.trace_ref,
  };
}

/**
 * Maps a raw Work Queue API event (snake_case) to the UI shape used across the
 * domain. Kept isolated so swapping the mock dataset for CT-4867's endpoint is a
 * one-file change. Uses `??` to tolerate camelCase/snake_case during rollout.
 */
export function mapApiEventToUi(raw = {}) {
  const type = raw.type ?? raw.event_type;
  // Single seam for the DEV-ONLY workflow-trace seed: every event passes through
  // here, so the 4 mappers stay untouched. Returns the detail unchanged unless
  // `wq_enrich_trace` is set in a dev build, and a real `render.trace` always
  // wins — see constants/traceFixtures.js for why no endpoint provides one.
  const detail = applyTraceFixture(displayEventType(type), mapDetail(raw.detail ?? {}));
  return {
    id: raw.id ?? raw.event_id,
    scope: raw.scope,
    type,
    category: raw.category,
    severity: resolveSeverity(raw, detail),
    title: raw.title,
    description: raw.description,
    refId: raw.ref_id ?? raw.refId,
    refLabel: raw.ref_label ?? raw.refLabel,
    actionLabel: raw.action_label ?? raw.actionLabel,
    studio: raw.studio,
    studioExtraCount: raw.studio_extra_count ?? raw.studioExtraCount ?? 0,
    status: raw.status,
    dueLabel: raw.due_label ?? raw.dueLabel,
    dueBucket: raw.due_bucket ?? raw.dueBucket,
    dueAt: raw.due_at ?? raw.dueAt ?? null,
    overdue: raw.overdue ?? false,
    isCustomer: resolveIsCustomer({
      type,
      isCustomer: raw.is_customer ?? raw.isCustomer,
      render: raw.render,
      category: raw.category,
      team: raw.team,
      title: raw.title,
      description: raw.description,
      situation: raw.situation,
      refLabel: raw.ref_label ?? raw.refLabel,
      destination: raw.destination,
      geClassification: detail.geClassification ?? detail.ge_classification,
      channel: detail.channel ?? detail.workflow,
    }),
    delegatedFrom: raw.delegated_from ?? raw.delegatedFrom ?? null,
    owner: mapOwner(raw.owner),
    missionCritical: raw.mission_critical ?? raw.missionCritical ?? false,
    blocks: {
      workflows: raw.blocks?.workflows ?? 0,
      agents: raw.blocks?.agents ?? 0,
    },
    commentsCount: raw.comments_count ?? raw.commentsCount ?? 0,
    situation: raw.situation,
    pipeline: raw.pipeline ?? [],
    destination: raw.destination,
    // Type-specific detail payload (continuation output, claims, break-glass, …).
    detail,
  };
}
